/* 166 §F.1 — THE ONE FAILURE LISTING, EXTRACTED RATHER THAN INVENTED AGAIN.
 *
 * The brief's instruction was "reuse the shell those files' own failure tables
 * use if one exists … do NOT invent a fifth". Censused first, as directed:
 *
 *   FolderPropagationModal.tsx:266-283   IS a shell — a bordered, scrolling
 *                                        box, one line per row (label left,
 *                                        reason right), a "+N more…" tail, and
 *                                        a heading with a warning triangle and
 *                                        a count. Written INLINE, so nothing
 *                                        else could mount it.
 *   AttachmentSaveModal.tsx:189          is NOT one — it marks each row of a
 *                                        list it already renders ("✓ saved",
 *                                        "⚠ failed") and toasts a summary.
 *                                        There is no list to mark in the email
 *                                        panel: the rows that failed are gone
 *                                        from the selection by then.
 *
 * So this is the first shell, MOVED here from the modal that had it, dress
 * unchanged to the pixel — the same margins, the same `maxHeight: 180`, the
 * same `FS.s11`/`FS.s10_5` sizes, the same flex row with an ellipsised label
 * and a non-shrinking reason. THE FOUR RULES 3: this commit moves it and
 * mounts it; it does not better it.
 *
 * WHAT IS A PROP AND WHY. Every WORD is: the two hosts have their own key
 * families (`folder_prop.*`, `caseEmails.*`) and a shared component that owned
 * the strings would have forced a new generic key family on both — the seed
 * only inserts, so that is three new rows per string for no gain. The
 * component owns the SHAPE; the caller owns the sentence.
 *
 * `tone` is the one thing the rows differ in: the propagation modal lists
 * SKIPPED rows (a muted reason — nothing went wrong, they were left alone)
 * above FAILED ones (red). The email panel has only failures.
 */
/* React is imported for `react/react-in-jsx-scope`, which the committed eslint
   config still enforces: a file the ratchet baseline does not know must have
   NO warnings at all (`scripts/lint/eslint-ratchet.js` — "a file NOT in the
   baseline may have no warnings"), so a new component pays its own way in.
   The runtime does not need it (the automatic JSX transform), and nothing here
   references it. */
import React from "react";
import { AlertTriangle } from "lucide-react";
import { RAD, FS } from "../lib/tokens";

/** The default cap, exported so a caller can count what it hides. */
export const FAILED_ROWS_MAX = 100;

export interface FailedRow {
  /** what the row IS — a folder's label, an email's subject. */
  label: string;
  /** why, in the reader's language. */
  reason: string;
  /** "warn" = muted (skipped, nothing wrong); default = red (failed). */
  tone?: "warn" | "fail";
  /** the reason's hover, for a stable identifier behind a human sentence. */
  reasonTitle?: string;
}

interface Props {
  /** the heading, already translated; the count is appended by this shell. */
  title: string;
  rows: readonly FailedRow[];
  /** how many rows render before the tail line. */
  max?: number;
  /** the tail line, already translated. Rendered only when rows are hidden;
   *  the CALLER counts them, with `max` (or `FAILED_ROWS_MAX`) — a callback
   *  here would be a named parameter in a function type, which the committed
   *  `no-unused-vars` reads as unused, and a new file must be warning-free. */
  moreLabel: string;
}

export default function FailedRowsList({ title, rows, max = FAILED_ROWS_MAX, moreLabel }: Readonly<Props>) {
  if (!rows.length) return null;
  const hidden = rows.length - max;
  return (
    <div style={{ margin: "6px 0 10px" }}>
      <div
        style={{
          fontSize: FS.s11_5,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 5,
          marginBottom: 4,
        }}
      >
        <AlertTriangle size={12} style={{ color: "var(--rd)" }} />
        {title} ({rows.length})
      </div>
      <div
        style={{
          maxHeight: 180,
          overflowY: "auto",
          border: "1px solid var(--br)",
          borderRadius: RAD.r6,
          padding: "4px 6px",
        }}
      >
        {rows.slice(0, max).map((r, i) => (
          <div
            key={i}
            style={{
              fontSize: FS.s11,
              padding: "2px 0",
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            {/* 212 §17 — A ROW KEEPS ITS NAME. The reason did not shrink
                (`flexShrink: 0`) and the label did, down to nothing, so a long
                list of missing subfolders took the whole line and the row lost
                its name (Settings' result: two rows). The label now keeps at
                least 40% of the line, ellipsised past it; the reason takes the
                rest and WRAPS onto more lines instead of pushing the name out. */}
            <span
              title={r.label}
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto", minWidth: "40%" }}
            >
              {r.label}
            </span>
            <span
              title={r.reasonTitle}
              style={{
                color: r.tone === "warn" ? "var(--tm)" : "var(--rd)",
                flex: "0 1 auto",
                minWidth: 0,
                textAlign: "right",
                overflowWrap: "anywhere",
                ...(r.reasonTitle ? { cursor: "help" } : {}),
              }}
            >
              {r.reason}
            </span>
          </div>
        ))}
        {hidden > 0 && (
          <div style={{ fontSize: FS.s10_5, color: "var(--tm)", padding: "3px 0" }}>
            {moreLabel}
          </div>
        )}
      </div>
    </div>
  );
}
